import React from "react";
import { Row, Col, Card, CardBody, Alert } from "reactstrap";

export interface StepCostProps {
  quota?: {
    quotaEnabled?: boolean;
    managedExternally: boolean;
    label?: string;
    creditsTotal?: number;
    creditsUsed?: number;
    creditsRemaining?: number;
    resetAt?: string;
    costEstimate?: {
      amount: string;
      currency?: string;
    };
  };
  audience: any;
}

const StepCost: React.FC<StepCostProps> = ({ quota, audience }) => {
  // Determine message as in StepReview
  let infoContent = null;
  const audienceCount =
    audience.type === "STATIC" ? audience.imported_count : audience.estimated_count;

  if (quota?.quotaEnabled) {
    const left = quota.creditsRemaining ?? 0;
    const willSendCount = Math.min(audienceCount, left);
    infoContent =
      left === 0 ? (
        <Alert color="danger" className="mb-3">
          <i className="ri-error-warning-line me-1" />
          <b>0</b> credits left for this integration. No messages will be sent.
        </Alert>
      ) : willSendCount < audienceCount ? (
        <Alert color="warning" className="mb-3">
          <i className="ri-error-warning-line me-1" />
          Only <b>{willSendCount}</b> out of {audienceCount} recipients will be reached due to quota ({left} left).
        </Alert>
      ) : (
        <Alert color="info" className="mb-3">
          <i className="ri-speed-up-line me-1" />
          Quota available: <b>{left}</b> / {quota.creditsTotal} credits left.
          <br />
          {quota.resetAt && (
            <span className="small text-muted">
              Quota resets on {new Date(quota.resetAt).toLocaleDateString()}.
            </span>
          )}
        </Alert>
      );
  } else if (quota?.costEstimate) {
    infoContent = (
      <Alert color="info" className="mb-3">
        <i className="ri-price-tag-3-line me-1" />
        Estimated cost for this campaign:{" "}
        <b>
          {quota.costEstimate.amount} {quota.costEstimate.currency || ""}
        </b>
        <br />
        <span className="small text-muted">
          Please ensure sufficient balance to cover this cost.
        </span>
      </Alert>
    );
  } else if (quota && !quota.quotaEnabled && !quota.costEstimate) {
    infoContent = (
      <Alert color="success" className="mb-3">
        <i className="ri-checkbox-circle-line me-1" />
        {quota.managedExternally
          ? (quota.label || <>This integration’s quota is <b>managed by the external service</b>. Limits may apply as per your provider.</>)
          : <>This integration has <b>unlimited</b> usage for now.</>
        }
      </Alert>
    );
  }

  return (
    <>
      <Row className="mb-4">
        <Col>
          <h4 className="fw-bold">Campaign Cost Estimate / Quota</h4>
        </Col>
      </Row>
      <Row className="gx-4 mb-5">
        <Col>
          {infoContent}
        </Col>
      </Row>
    </>
  );
};

export default StepCost;
